// src/pages/InventoryDetail.js
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import {
  Typography,
  Paper,
  Button,
  Grid,
  IconButton,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  CircularProgress,
  TextField,
} from '@mui/material';
import { ContentCopy } from '@mui/icons-material';
import TanStackTable from '../components/TanStackTable';
import { getInventoryByProductId, getInventoryMovements, updateInventory } from '../services/inventoryService';
import { useLoading } from '../contexts/LoadingContext';
import BackButton from '../components/BackButton';
import { DATE_FORMAT_DISPLAY, formatDateTimeToStandard } from '../utils/DateFormatter';

const InventoryDetail = () => {
  const { orgId, productId } = useParams();
  const [inventoryItem, setInventoryItem] = useState(null);
  const [error, setError] = useState('');
  const { isLoading, setIsLoading } = useLoading();
  const [movements, setMovements] = useState([]);
  const [loadingMovements] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editableData, setEditableData] = useState({});

  useEffect(() => {
    if (orgId && productId) {
      setIsLoading(true);
      Promise.all([
        getInventoryByProductId(orgId, productId),
        getInventoryMovements(orgId, productId) // Updated to pass both orgId and productId
      ])
        .then(([inventory, movementData]) => {
          setInventoryItem(inventory);
          setMovements(movementData);
        })
        .catch((err) => setError(err.message))
        .finally(() => setIsLoading(false));
    }
  }, [orgId, productId, setIsLoading]);

  useEffect(() => {
    if (inventoryItem) {
      setEditableData({
        quantity_on_hand: inventoryItem.quantity_on_hand,
        minimum_stock_level: inventoryItem.minimum_stock_level,
        maximum_stock_level: inventoryItem.maximum_stock_level,
        reorder_point: inventoryItem.reorder_point,
      });
    }
  }, [inventoryItem]);

  if (error) {
    return <Typography variant="h6" color="error">{error}</Typography>;
  }

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!inventoryItem) {
    return <Typography variant="h6">Loading...</Typography>;
  }

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      await updateInventory(orgId, productId, {
        ...editableData,
        meta: {
          source: 'Edit Inventory button',
          user_action: 'manual_update'
        }
      });
      setIsEditing(false);
      // Refresh data
      const [updatedInventory, updatedMovements] = await Promise.all([
        getInventoryByProductId(orgId, productId),
        getInventoryMovements(orgId, productId)
      ]);
      setInventoryItem(updatedInventory);
      setMovements(updatedMovements);
    } catch (error) {
      console.error('Error updating inventory:', error);
    }
  };

  // Define main fields
  const mainFields = {
    'Product ID': inventoryItem.product.id,
    'Product Name': inventoryItem.product.product_name,
    'Category': inventoryItem.product.category,
    'Quantity on Hand': inventoryItem.quantity_on_hand,
    'Unit of Measure': inventoryItem.product.unit_of_measure,
  };

  // Define additional fields
  const additionalFields = {
    'Minimum Stock Level': inventoryItem.minimum_stock_level,
    'Maximum Stock Level': inventoryItem.maximum_stock_level,
    'Reorder Point': inventoryItem.reorder_point,
    'Default Order Quantity': inventoryItem.default_order_quantity,
    'Last Unit Cost': inventoryItem.last_unit_cost,
    'Last Unit Price': inventoryItem.last_unit_price,
    'Excess Inventory': inventoryItem.excess_inventory ? 'Yes' : 'No',
  };

  const movementColumns = [
    { 
      header: 'Total Quantity', 
      accessorKey: 'quantity'
    },
    { 
      header: 'Movement Type', 
      accessorKey: 'movement_type',
      cell: ({ getValue }) => {
        const value = getValue();
        return value.charAt(0).toUpperCase() + value.slice(1);
      }
    },
    { 
      header: 'Quantity Change', 
      accessorKey: 'quantity_change',
      cell: ({ getValue }) => {
        const value = getValue();
        return (
          <span style={{ color: value > 0 ? 'green' : 'red' }}>
            {value}
          </span>
        );
      }
    },
    { 
      header: 'Reason', 
      accessorKey: 'reason'
    },
    { 
      header: `Date (${DATE_FORMAT_DISPLAY})`, 
      accessorKey: 'date_of_movement',
      cell: ({ getValue }) => formatDateTimeToStandard(getValue())
    }
  ];

  return (
    <Box 
      sx={{
        height: 'calc(100vh - 84px)', // Fixed height for outer container
        overflow: 'hidden', // Prevent scrolling on outer container
      }}
    >
      <Paper 
        sx={{ 
          p: 3, 
          bgcolor: 'background.paper', 
          color: 'text.primary',
          backdropFilter: 'saturate(140%) blur(6px)',
          border: '1px solid',
          borderColor: 'divider',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'auto',
        }}
      >
        <BackButton />
        {/* Fixed Header Section */}
        <Box sx={{ mb: 3, flexShrink: 0 }}>
          <Typography variant="h5" gutterBottom>
            {inventoryItem.product.product_name}
          </Typography>
          {!isEditing ? (
            <Button variant="contained" onClick={handleEdit} sx={{ mb: 2 }}>
              Edit Inventory
            </Button>
          ) : (
            <Button variant="contained" onClick={handleSave} sx={{ mb: 2 }}>
              Save Changes
            </Button>
          )}
        </Box>

        {/* Content Section */}
        <Box 
          sx={{ 
            display: 'flex',
            flexDirection: 'column',
            gap: 3,
          }}
        >
          {/* Main Fields Section */}
          {!isEditing ? (
            <Grid container spacing={2}>
              {Object.entries(mainFields).map(([key, value]) => (
                <React.Fragment key={key}>
                  <Grid item xs={12} sm={6} md={4}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                      {key}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={5} md={7}>
                    <Typography variant="body1">{value}</Typography>
                  </Grid>
                  <Grid item xs={12} sm={1} md={1} display="flex" alignItems="center" justifyContent="flex-end">
                    <Tooltip title="Copy">
                      <IconButton onClick={() => handleCopy(value)}>
                        <ContentCopy />
                      </IconButton>
                    </Tooltip>
                  </Grid>
                </React.Fragment>
              ))}
            </Grid>
          ) : (
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  label="Quantity on Hand"
                  type="number"
                  value={editableData.quantity_on_hand || ''}
                  onChange={(e) => setEditableData({ ...editableData, quantity_on_hand: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Minimum Stock Level"
                  type="number"
                  value={editableData.minimum_stock_level || ''}
                  onChange={(e) => setEditableData({ ...editableData, minimum_stock_level: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Maximum Stock Level"
                  type="number"
                  value={editableData.maximum_stock_level || ''}
                  onChange={(e) => setEditableData({ ...editableData, maximum_stock_level: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label="Reorder Point"
                  type="number"
                  value={editableData.reorder_point || ''}
                  onChange={(e) => setEditableData({ ...editableData, reorder_point: parseInt(e.target.value) })}
                  fullWidth
                />
              </Grid>
            </Grid>
          )}

          {/* Detailed Information Section */}
          {Object.keys(additionalFields).length > 0 && (
            <Box>
              <Typography variant="h6" gutterBottom>
                Detailed Information
              </Typography>
              <TableContainer 
                component={Paper} 
                sx={{ 
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Field</TableCell>
                      <TableCell>Value</TableCell>
                      <TableCell>Copy</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {Object.entries(additionalFields).map(([key, value]) => (
                      <TableRow key={key}>
                        <TableCell>{key.replace(/_/g, ' ')}</TableCell>
                        <TableCell>{value !== null && value !== undefined ? value.toString() : 'N/A'}</TableCell>
                        <TableCell>
                          <Tooltip title="Copy">
                            <IconButton onClick={() => handleCopy(value)}>
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {/* Movement Log Section */}
          <Box sx={{ minHeight: 400 }}> {/* Set minHeight instead of fixed height */}
            <Typography variant="h6" gutterBottom>
              Inventory Movement Log
            </Typography>
            {loadingMovements ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
                <CircularProgress />
              </Box>
            ) : (
              <Box 
                sx={{ 
                  height: 400, // Fixed height for AG Grid
                  '.ag-theme-alpine-dark': {
                    '--ag-header-height': '48px',
                    '--ag-row-height': '48px',
                  },
                }}
              >
                <TanStackTable
                  data={movements}
                  columns={movementColumns}
                />
              </Box>
            )}
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default InventoryDetail;
